import { FC, useEffect, useState } from 'react'
import { Button, Input, Modal } from 'antd'
import moment from 'moment'
import { globalToaster } from 'components/elements/global'
import {
 useCreateDomainMutation,
 useLazyGetDomainStatusQuery,
 useScanDomainMutation,
} from '../api'
import {
 TDnsRecord,
 TDomainType,
 TPrimaryDomain,
 TRegistrarInstructions,
} from '../types'
import {
 DEFAULT_NAMESERVERS,
 PURCHASE_DOMAIN_URL,
 getErrorMessage,
 isConnected,
 toRecordBody,
} from '../utils'
import RecordsTable from './RecordsTable'
import RecordModal from './RecordModal'
import DeleteRecordModal from './DeleteRecordModal'
import NameserverRow from './NameserverRow'

type TStep = 1 | 2 | 3

type Props = {
 visible: boolean
 mode: TDomainType
 // Current primary domain: "Change domain" replaces it; redirects target it
 primary: TPrimaryDomain | null
 // "View setup instructions" for a pending domain: open straight on step 3
 resumeDomain?: string | null
 onClose: () => void
}

// Accepts "https://www.Example.com/" style input; returns "www.example.com"
const normalizeDomain = (input: string): string =>
 input
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/\/.*$/, '')

const DOMAIN_PATTERN = /^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/

const STEP1_SUBTEXT: Record<TDomainType, string> = {
 primary:
  'Enter the domain you already own. We’ll check what’s currently set up before you change anything.',
 redirect:
  'Enter the other domain you own. We’ll check what’s currently set up on it before changing anything, then set it to forward here.',
}

const StepHead: FC<{ num: number; title: string }> = ({ num, title }) => (
 <div className="step-head">
  <div className="step-num">
   {/* Done steps show a check instead of the number */}
   <span className="num">{num}</span>
   <i className="fas fa-check" />
  </div>
  <div className="step-title">{title}</div>
 </div>
)

// 3-step connect flow shared by "Connect/Change domain" and "Add redirect
// domain": scan → review records (client-side) → nameserver instructions
const DomainWizard: FC<Props> = ({
 visible,
 mode,
 primary,
 resumeDomain,
 onClose,
}) => {
 const [step, setStep] = useState<TStep>(1)
 const [domainInput, setDomainInput] = useState('')
 const [inputError, setInputError] = useState('')
 const [domain, setDomain] = useState('')
 const [records, setRecords] = useState<TDnsRecord[]>([])
 const [instructions, setInstructions] = useState<TRegistrarInstructions>()
 const [nameservers, setNameservers] = useState(DEFAULT_NAMESERVERS)
 const [checkMessage, setCheckMessage] = useState('')
 const [connected, setConnected] = useState(false)
 // "Not your registrar?" swaps the detected registrar's steps for generic ones
 const [showGeneric, setShowGeneric] = useState(false)
 // Record modal: null = closed, { index: -1 } = add, otherwise edit row
 const [editing, setEditing] = useState<{
  record: TDnsRecord | null
  index: number
 } | null>(null)
 const [deleting, setDeleting] = useState<number | null>(null)
 // Primary as it was when the wizard opened: "Change domain" replaces it
 // mid-flow, which refetches the page status and would change the prop
 const [target, setTarget] = useState<TPrimaryDomain | null>(null)
 // Fixed at open so the title stays "Change your domain" after the replace
 const [isChange, setIsChange] = useState(false)

 const [scanDomain, { isLoading: isScanning }] = useScanDomainMutation()
 const [createDomain, { isLoading: isCreating }] = useCreateDomainMutation()
 const [fetchStatus, { isFetching: isChecking }] = useLazyGetDomainStatusQuery()

 const isResume = !!resumeDomain

 // Fresh wizard every time it opens ("Change domain" never reuses old steps)
 useEffect(() => {
  if (!visible) return
  setDomainInput('')
  setInputError('')
  setRecords([])
  setInstructions(undefined)
  setNameservers(DEFAULT_NAMESERVERS)
  setCheckMessage('')
  setConnected(false)
  setShowGeneric(false)
  setEditing(null)
  setDeleting(null)
  setTarget(primary)
  setIsChange(!!primary)
  if (resumeDomain) {
   setDomain(resumeDomain)
   setStep(3)
   // Instructions aren't stored server-side; the scan is stateless, so
   // re-run it to show the registrar steps again
   scanDomain({ domain: resumeDomain })
    .unwrap()
    .then((res) => setInstructions(res.instructions))
    .catch(() => undefined)
  } else {
   setDomain('')
   setStep(1)
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [visible, resumeDomain])

 const onScan = async (): Promise<void> => {
  const value = normalizeDomain(domainInput)
  if (!DOMAIN_PATTERN.test(value)) {
   setInputError('Enter a valid domain, e.g. zenahomes.com')
   return
  }
  setInputError('')
  try {
   const res = await scanDomain({ domain: value }).unwrap()
   setDomain(value)
   setRecords(res.records ?? [])
   setInstructions(res.instructions)
   setStep(2)
  } catch (err) {
   globalToaster({
    type: 'error',
    content: getErrorMessage(err, 'Could not scan this domain. Try again.'),
   })
  }
 }

 // Persists the domain with the reviewed records (first API write)
 const onContinue = async (): Promise<void> => {
  try {
   // "Change domain": the backend replaces the old primary on create
   const res = await createDomain({
    domain,
    type: mode,
    redirect_target_domain_id: mode === 'redirect' ? target?.id ?? null : null,
    records: records.map(toRecordBody),
   }).unwrap()
   if (res?.nameservers?.length) setNameservers(res.nameservers)
   setStep(3)
  } catch (err) {
   globalToaster({
    type: 'error',
    content: getErrorMessage(err, 'Could not connect this domain. Try again.'),
   })
  }
 }

 // Reflects the last scheduled check only; it doesn't re-check DNS live
 const onCheckNow = async (): Promise<void> => {
  try {
   const status = await fetchStatus().unwrap()
   const match =
    mode === 'primary'
     ? status.primary
     : status.redirects?.find((r) => r.domain === domain)
   if (isConnected(match?.ns_status)) {
    setConnected(true)
    return
   }
   const lastChecked = match?.last_checked_at
    ? `last checked ${moment(match.last_checked_at).fromNow()}`
    : 'not checked yet'
   setCheckMessage(
    `Still waiting — ${lastChecked}. We'll email you the moment it's connected, no need to keep checking.`,
   )
  } catch (err) {
   globalToaster({
    type: 'error',
    content: getErrorMessage(err, 'Could not check the status. Try again.'),
   })
  }
 }

 const onSaveRecord = (record: TDnsRecord): void => {
  if (!editing) return
  setRecords((rows) =>
   editing.index < 0
    ? [...rows, record]
    : rows.map((row, i) => (i === editing.index ? record : row)),
  )
  setEditing(null)
 }

 const onDeleteRecord = (): void => {
  setRecords((rows) => rows.filter((_, i) => i !== deleting))
  setDeleting(null)
 }

 const title = ((): string => {
  if (isResume) return 'Domain setup — nameserver instructions'
  if (mode === 'redirect') return 'Add a redirecting domain'
  return isChange ? 'Change your domain' : 'Connect your domain'
 })()

 // Banner name: the server's display name, else the raw WHOIS registrar
 // (e.g. "GoDaddy.com, LLC") when it detected one but has no tailored steps
 const registrarName = instructions?.display_name || instructions?.registrar
 // Tailored steps exist only for a matched registrar (display_name set)
 const hasRegistrarSteps = !!instructions?.display_name

 const hasRootA = records.some((r) => r.type === 'A' && r.host === '@')
 const stepClass = (n: TStep): string => {
  if (n === step) return 'step active'
  return n < step ? 'step done' : 'step hidden'
 }

 return (
  <Modal
   visible={visible}
   title={title}
   onCancel={onClose}
   footer={null}
   width={620}
   className="domain-wizard"
   destroyOnClose
   maskClosable={false}
  >
   {/* STEP 1 — add domain */}
   <div className={stepClass(1)}>
    <StepHead num={1} title="Add your domain" />
    <div className="step-content">
     {step === 1 ? (
      <>
       <p className="sub">{STEP1_SUBTEXT[mode]}</p>
       <label className="field-label" htmlFor="domain-input">
        Domain name
        <Input
         id="domain-input"
         value={domainInput}
         placeholder="zenahomes.com"
         status={inputError ? 'error' : undefined}
         onChange={(e) => setDomainInput(e.target.value)}
         onPressEnter={onScan}
        />
       </label>
       {inputError && <div className="field-error">{inputError}</div>}
       <Button
        type="primary"
        className="step-btn"
        loading={isScanning}
        icon={<i className="fas fa-search" />}
        onClick={onScan}
       >
        Scan domain
       </Button>
       {mode === 'primary' && (
        <div>
         <a
          href={PURCHASE_DOMAIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="purchase-link"
         >
          Don&apos;t have a domain? Purchase one here
         </a>
        </div>
       )}
      </>
     ) : (
      <span className="step-summary">
       <b>{domain}</b>
      </span>
     )}
    </div>
   </div>

   {/* STEP 2 — review records (client-side until "Looks good, continue") */}
   <div className={stepClass(2)}>
    <StepHead num={2} title="Review what's currently set up" />
    <div className="step-content">
     {step === 2 && (
      <>
       <p className="sub">
        We found these records at your current DNS provider. Anything kept here
        will keep working exactly as-is after the switch — remove anything you
        don&apos;t recognize, or add something we missed.
       </p>
       {records.length ? (
        <RecordsTable
         records={records}
         onEdit={(record, index) => setEditing({ record, index })}
         onDelete={(_, index) => setDeleting(index)}
        />
       ) : (
        <div className="records-empty">No existing records found.</div>
       )}
       <button
        type="button"
        className="add-record-link"
        onClick={() => setEditing({ record: null, index: -1 })}
       >
        <i className="fas fa-plus" /> Add a record we missed
       </button>
       <div className="info-box">
        <i className="fas fa-info-circle" />
        <span>
         {hasRootA
          ? 'The A record above currently points to your old website. It will stay exactly like this until you choose to make your new AgentRoof site live — nothing switches automatically.'
          : 'Everything kept here keeps working exactly as it does today — nothing switches automatically.'}
        </span>
       </div>
       <Button
        type="primary"
        className="step-btn"
        loading={isCreating}
        onClick={onContinue}
       >
        Looks good, continue
       </Button>
      </>
     )}
     {step === 3 && (
      <span className="step-summary">
       {isResume ? (
        'Records reviewed'
       ) : (
        <>
         <b>
          {records.length} record{records.length === 1 ? '' : 's'}
         </b>{' '}
         kept as-is
        </>
       )}
      </span>
     )}
    </div>
   </div>

   {/* STEP 3 — nameserver instructions (server-resolved) */}
   <div className={stepClass(3)}>
    <StepHead num={3} title="Point your domain to AgentRoof" />
    <div className="step-content">
     {step === 3 && !connected && (
      <>
       <p className="sub">
        Go to wherever you registered this domain and replace the current
        nameservers with these three. Click a value to copy it.
       </p>
       {registrarName && (
        <div className="registrar-banner">
         <i className="fas fa-check-circle" />
         <span>
          We detected this domain is registered with <b>{registrarName}</b>.{' '}
          {hasRegistrarSteps && (
           <button
            type="button"
            className="link-btn"
            onClick={() => setShowGeneric((v) => !v)}
           >
            {showGeneric ? 'Show registrar steps' : 'Not your registrar?'}
           </button>
          )}
         </span>
        </div>
       )}
       {showGeneric && (
        <div className="registrar-steps">
         <p className="sub generic-steps">
          Log in to whichever company you registered this domain with, find the
          nameserver or &quot;DNS management&quot; setting, and replace the
          existing values with these three.
         </p>
        </div>
       )}
       {!showGeneric && !!instructions?.steps?.length && (
        <div className="registrar-steps">
         <ol>
          {instructions.steps.map((text) => (
           <li key={text}>{text}</li>
          ))}
         </ol>
         {instructions.display_name && instructions.help_url && (
          <a
           href={instructions.help_url}
           target="_blank"
           rel="noopener noreferrer"
          >
           View {instructions.display_name}&apos;s help article →
          </a>
         )}
        </div>
       )}
       {nameservers.map((ns) => (
        <NameserverRow key={ns} value={ns} />
       ))}
       <span className="status-pill pending">
        <i className="fas fa-clock" /> Waiting for the change
       </span>
       <p className="sub pending-note">
        This can take anywhere from a few minutes to 24 hours depending on your
        registrar. We check automatically and will email you the moment
        it&apos;s done — everything currently working on this domain stays
        working the entire time.
       </p>
       <Button
        loading={isChecking}
        icon={<i className="fas fa-sync-alt" />}
        onClick={onCheckNow}
       >
        {isChecking ? 'Checking...' : 'Check now'}
       </Button>
       {checkMessage && (
        <div className="info-box check-result">
         <i className="fas fa-clock" />
         <span>{checkMessage}</span>
        </div>
       )}
      </>
     )}
     {step === 3 && connected && (
      <div className="success-panel">
       <div className="icon-circle done">
        <i className="fas fa-check" />
       </div>
       <h3>Connected</h3>
       <p>
        {mode === 'primary'
         ? `${domain} is now pointing to AgentRoof.`
         : `${domain} now redirects to ${
            target?.domain ?? 'your main domain'
           }.`}
       </p>
       <Button type="primary" className="step-btn" onClick={onClose}>
        Done
       </Button>
      </div>
     )}
    </div>
   </div>

   <RecordModal
    visible={!!editing}
    record={editing?.record}
    onCancel={() => setEditing(null)}
    onSave={onSaveRecord}
   />
   <DeleteRecordModal
    record={deleting != null ? records[deleting] : null}
    onCancel={() => setDeleting(null)}
    onConfirm={onDeleteRecord}
   />
  </Modal>
 )
}

export default DomainWizard
